import { MouseEventHandler, useContext, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { EvaluationIcon } from "components/SVGs";
import Pagination from "components/Pagination/index";
import { useHistory } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Print from "components/Print";
import ExportReport from "components/ExportReport";
import { MasterDataContext } from "contexts/MasterDataContext";
import SelectInput from "components/SelectInput";
import {
  selectReport,
  reportSet,
  domainSet,
  selectDomain,
  classSet,
  selectGrade,
  selectClass,
  selectPagination,
  paginationSet,
} from "features/classPerformance";
import { toDomainOptions, toClassOptions } from "utils/ReportsHelpers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useTranslation } from "react-i18next";
import {
  useFetchClassesOfGradeQuery,
  useFetchLanguageDomainsQuery,
} from "services/common";
import { useFetchClassAssessmentsQuery } from "services/evaluation";
import {
  assessmentsSet,
  completedEvaluationTabOption,
  isRedirectingSet,
  pendingEvaluationTabOption,
  tabSet,
  totalCompletedAssessmentsCount,
  totalPendingAssessmentsCount,
} from "features/evaluation";
import { Assessment, IOption } from "features/common/Interfaces";

const reportsType = [
  { label: Assessment.FORMATIVE, value: Assessment.FORMATIVE },
  { label: Assessment.BASELINE, value: Assessment.BASELINE },
];

function Toolbar({ setPrinting }: { setPrinting(value: boolean): void }) {
  const { t } = useTranslation();
  const selectedGrade = useAppSelector(selectGrade);
  const selectedClass = useAppSelector(selectClass);
  const selectedReport = useAppSelector(selectReport);
  const selectedDomain = useAppSelector(selectDomain);
  const pendingAssessmentsCount = useAppSelector(totalPendingAssessmentsCount);
  const completedAssessmentsCount = useAppSelector(
    totalCompletedAssessmentsCount
  );
  const report = useAppSelector(selectReport);
  const pagination = useAppSelector(selectPagination);
  const [domains, setDomains] = useState<IOption[]>([]);
  const [classes, setClasses] = useState<IOption[]>([]);
  const dispatch = useAppDispatch();
  const { data: languageDomainsData } = useFetchLanguageDomainsQuery();
  const { data: classesOfGradeData } = useFetchClassesOfGradeQuery(
    selectedGrade?.id!,
    { skip: !selectedGrade?.id }
  );
  const { data: classAssessmentData } = useFetchClassAssessmentsQuery(
    selectedClass?.value as number,
    {
      skip: !selectedClass?.value,
    }
  );

  useEffect(() => {
    if (classAssessmentData?._statusCode === 200) {
      const data = classAssessmentData._entity?._classOfflineAssessments;
      const assessments = data.map((assessment) => ({
        loId: assessment._loId,
        loTitle: assessment._loTitle,
        lessonTitle: assessment._lessonTitle ?? assessment._loTitle ?? "",
        loCode: assessment._loCode,
        thumb: assessment._loThumbURL,
        domainName: assessment._domainName,
        assessmentType: assessment._assessmentType,
        studentDetails: assessment._studentDetails.map((item) => ({
          name: item._name,
          id: item._id,
          evaluationStatus: item._evaluationStatus,
          avatar: item._avatar,
        })),
      }));
      dispatch(assessmentsSet(assessments));
    }
  }, [classAssessmentData]);

  useEffect(() => {
    if (languageDomainsData?._statusCode === 200) {
      const domains = toDomainOptions(languageDomainsData._entity);
      dispatch(domainSet(domains[0]));
      setDomains(domains);
    }
  }, [languageDomainsData]);
  useEffect(() => {
    if (classesOfGradeData?._statusCode === 200) {
      const classes = toClassOptions(classesOfGradeData._entity);
      dispatch(classSet(classes[0]));
      setClasses(classes);
    }
  }, [classesOfGradeData]);

  const history = useHistory();
  const { logEvent } = useContext(MasterDataContext);

  const getPage = (value: number) => {
    dispatch(paginationSet({ ...pagination, currentPage: value }));
  };
  const evaluateHandler: MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    localStorage.setItem("grade-evaluation", JSON.stringify(selectedGrade));
    localStorage.setItem("class-evaluation", JSON.stringify(selectedClass));
    dispatch(isRedirectingSet(true));
    if (pendingAssessmentsCount && pendingAssessmentsCount > 0)
      dispatch(tabSet(pendingEvaluationTabOption));
    else dispatch(tabSet(completedEvaluationTabOption));

    history.push("/evaluation");
  };

  const setIsPrint = (printState: boolean) => {
    logEvent("ClassReportPrintOut", {
      ClassId: selectedClass?.value!,
    });
    setPrinting(printState);
  };

  const allDomains = selectedDomain?.value === -1;
  const isThereSubmittedAsessements =
    (pendingAssessmentsCount ?? 0) + (completedAssessmentsCount ?? 0) > 0;

  return (
    <>
      {classes?.length > 0 ? (
        <Grid
          container
          justifyContent="space-evenly"
          alignItems="stretch"
          spacing={2}
          sx={{ padding: "0 0.5rem 0.5rem" }}
        >
          <Grid item xs={3} md={3} xl={2}>
            <SelectInput
              defaultValue={selectedReport.value}
              valueList={structuredClone(reportsType).map((type) => {
                type.label = t(type.label);
                return type;
              })}
              getOption={({ currentTarget }) => {
                dispatch(reportSet(currentTarget));
                dispatch(
                  paginationSet({
                    ...pagination,
                    currentPage: 1,
                  })
                );
              }}
            />
          </Grid>
          <Grid item xs={3} md={3} xl={2}>
            <SelectInput
              defaultValue={selectedClass?.value}
              valueList={classes}
              getOption={({ currentTarget }) => {
                dispatch(classSet(currentTarget));
                dispatch(paginationSet({ ...pagination, currentPage: 1 }));
              }}
            />
          </Grid>
          <Grid item xs={3} md={3} xl={2}>
            <SelectInput
              defaultValue={selectedDomain ? selectedDomain.value : undefined}
              valueList={structuredClone(domains).map((domain) => {
                domain.label = t(domain.label);
                return domain;
              })}
              getOption={({ currentTarget }) => {
                dispatch(domainSet(currentTarget));
                dispatch(paginationSet({ ...pagination, currentPage: 1 }));
              }}
            />
          </Grid>
          <Grid
            item
            xs
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Grid container justifyContent="flex-end" alignItems="center">
              {!allDomains && selectedReport.value !== "baseline" && (
                <Grid item>
                  <Pagination
                    total={pagination?.totalPages}
                    getPage={getPage}
                    defaultPage={pagination?.currentPage}
                  />
                </Grid>
              )}
              {isThereSubmittedAsessements ? (
                <Grid
                  item
                  onClick={evaluateHandler}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    margin: "0 10px",
                  }}
                >
                  <Badge
                    badgeContent={pendingAssessmentsCount}
                    sx={{
                      "& .MuiBadge-colorPrimary": {
                        background: "rgb(255 255 255)",
                        color: "rgb(98 195 238)",
                        border: "1px solid rgb(98 195 238)",
                        fontWeight: "bold",
                      },
                    }}
                    color="primary"
                  >
                    <EvaluationIcon style={{ color: "rgb(98 195 238)" }} />
                  </Badge>
                </Grid>
              ) : null}
              {allDomains && selectedClass ? (
                <Grid
                  item
                  sx={{
                    display: "flex",
                    alingItem: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <ExportReport
                    style={{ margin: "0 15px" }}
                    selectedClass={selectedClass}
                    type={
                      report?.value === Assessment.FORMATIVE
                        ? Assessment.FORMATIVE
                        : Assessment.BASELINE
                    }
                  />
                  <Print setIsPrint={setIsPrint} />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </>
  );
}

export default Toolbar;
